import { useState, useCallback, useEffect } from "react";
import type { ArborInspectionDetail } from "@/types/arboricultural-inspection";
import { inspectionApi } from "@/lib/api";
import { computeFileSha256 } from "@/lib/fileChecksum";
import { hasPermission } from "@/lib/auth";
import { photoCategoryLabel, photoGroupLabel } from "@/config/photo-categories";
import PhotoCategorySelect from "@/components/shared/PhotoCategorySelect";

interface Props {
  inspectionId: number | null;
  evidence: ArborInspectionDetail["evidence"];
  onEvidenceChange: () => void;
  workflowStatus?: string;
}


// Mesmos limites do servidor e do app: video ate 150 MB, demais ate 160 MB.
const MAX_BYTES = 160 * 1024 * 1024;
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;
// Acima disso o hash no navegador fica pesado; o servidor calcula de qualquer forma.
const MAX_CLIENT_HASH_BYTES = 20 * 1024 * 1024;

type Evidence = ArborInspectionDetail["evidence"][number];

function kindOf(mime: string | null): "image" | "video" | "audio" | "other" {
  if (mime?.startsWith("image/")) return "image";
  if (mime?.startsWith("video/")) return "video";
  if (mime?.startsWith("audio/")) return "audio";
  return "other";
}

function mb(bytes: number | null) {
  if (!bytes) return "";
  const v = bytes / 1_048_576;
  return `${v < 10 ? v.toFixed(1).replace(".", ",") : Math.round(v)} MB`;
}

// Conteudo da evidencia carregado com o token (o arquivo so e servido pela API).
function EvidencePreview({ inspectionId, ev }: { inspectionId: number; ev: Evidence }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const kind = kindOf(ev.mimeType);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    inspectionApi
      .evidenceFileUrl(inspectionId, ev.id)
      .then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        if (u) setUrl(u);
        else setFailed(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [inspectionId, ev.id]);

  if (!url) {
    const icon = kind === "video" ? "ri-video-line" : kind === "audio" ? "ri-mic-line" : "ri-image-line";
    return (
      <div className="aspect-square flex flex-col items-center justify-center gap-1 bg-background-100">
        <i className={`${failed ? "ri-error-warning-line" : icon} text-3xl text-foreground-300`}></i>
        {failed && <span className="text-[10px] text-foreground-400">Arquivo indisponível</span>}
      </div>
    );
  }
  if (kind === "image") return <img src={url} alt={ev.description ?? "Evidência"} className="aspect-square w-full object-cover" />;
  if (kind === "video") return <video src={url} controls className="aspect-square w-full bg-black object-contain" />;
  if (kind === "audio") {
    return (
      <div className="aspect-square flex flex-col items-center justify-center gap-2 bg-background-100 px-2">
        <i className="ri-mic-line text-3xl text-primary-500"></i>
        <audio src={url} controls className="w-full" />
      </div>
    );
  }
  return (
    <a href={url} download={ev.originalFilename ?? undefined} className="aspect-square flex items-center justify-center bg-background-100 text-xs text-primary-600">
      Baixar arquivo
    </a>
  );
}

export default function StepEvidencias({ inspectionId, evidence, onEvidenceChange, workflowStatus }: Props) {
  // Enquanto o tecnico coleta (rascunho, em andamento, devolvida) a evidencia se envia e se exclui.
  // Depois do envio ela integra o registro: so se INVALIDA, com motivo, e quem revisa.
  const editable = !workflowStatus || ["DRAFT", "IN_PROGRESS", "RETURNED"].includes(workflowStatus);
  const canInvalidate = !editable && workflowStatus !== "CANCELLED" && hasPermission("inspection.homologate");
  const [invalidating, setInvalidating] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("GENERAL_TREE");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!inspectionId) return;
      const kind = kindOf(file.type);
      if (kind === "other") {
        setError("Envie uma foto, um vídeo ou um áudio.");
        return;
      }
      const limit = kind === "video" ? MAX_VIDEO_BYTES : MAX_BYTES;
      if (file.size > limit) {
        setError(`Arquivo de ${mb(file.size)}. O limite é ${mb(limit)}.`);
        return;
      }
      setUploading(true);
      setError(null);
      try {
        const checksum = file.size <= MAX_CLIENT_HASH_BYTES ? await computeFileSha256(file) : null;
        await inspectionApi.uploadEvidence(inspectionId, file, {
          evidenceType: selectedType,
          description: description || undefined,
          clientChecksumSha256: checksum,
        });
        setDescription("");
        onEvidenceChange();
      } catch (err) {
        setError(err instanceof Error ? `Não foi possível enviar: ${err.message}` : "Erro ao enviar evidência.");
      } finally {
        setUploading(false);
      }
    },
    [inspectionId, selectedType, description, onEvidenceChange],
  );

  const handleUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (file) void uploadFile(file);
    },
    [uploadFile],
  );

  const handleDelete = useCallback(
    async (evidenceId: number) => {
      if (!inspectionId) return;
      if (!window.confirm("Remover esta evidência da vistoria?")) return;
      try {
        await inspectionApi.deleteEvidence(inspectionId, evidenceId);
        onEvidenceChange();
      } catch {
        setError("Erro ao remover evidência.");
      }
    },
    [inspectionId, onEvidenceChange],
  );

  const handleInvalidate = useCallback(
    async (evidenceId: number) => {
      if (!inspectionId) return;
      try {
        await inspectionApi.invalidateEvidence(inspectionId, evidenceId, reason.trim());
        setInvalidating(null);
        setReason("");
        setError(null);
        onEvidenceChange();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Não foi possível invalidar a evidência.");
      }
    },
    [inspectionId, reason, onEvidenceChange],
  );

  if (!inspectionId) {
    return (
      <div className="bg-white rounded-xl border border-background-200/70 p-6 text-center">
        <i className="ri-save-line text-3xl text-foreground-300"></i>
        <p className="text-sm text-foreground-500 mt-2">
          Salve o rascunho primeiro para habilitar o upload de evidências.
        </p>
      </div>
    );
  }

  const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
  const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

  return (
    <div className="space-y-5">
      {/* Upload */}
      {!editable && (
        <div className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-xs text-foreground-600 flex gap-2">
          <i className="ri-lock-line mt-0.5"></i>
          <span>
            Esta vistoria foi enviada: as evidências fazem parte do registro e não podem ser excluídas nem acrescentadas.
            {canInvalidate
              ? " Para retirar uma evidência do registro, invalide-a informando o motivo; o arquivo e o hash continuam guardados."
              : " Se algo precisa mudar, o revisor deve devolver a vistoria."}
          </span>
        </div>
      )}
      <div className={`bg-white rounded-xl border border-background-200/70 p-4 space-y-3 ${editable ? "" : "hidden"}`}>
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-camera-line text-primary-500"></i>
          Enviar Evidência
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">O que a foto mostra</label>
            <PhotoCategorySelect value={selectedType} onChange={setSelectedType} className={selectCls} ariaLabel="O que a foto mostra" />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">Descrição</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição da evidência..."
              className={inputCls}
            />
          </div>
        </div>
        {uploading ? (
          <div className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-primary-300 bg-primary-50/50">
            <i className="ri-loader-4-line animate-spin text-lg text-primary-500"></i>
            <span className="text-sm text-foreground-500">Enviando...</span>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* capture abre a camera traseira direto no celular/tablet; no computador vira seletor de arquivo. */}
            <CaptureButton icon="ri-camera-line" label="Tirar foto" accept="image/*" capture onChange={handleUpload} />
            <CaptureButton icon="ri-video-line" label="Gravar vídeo" accept="video/*" capture onChange={handleUpload} />
            <AudioRecorder onRecorded={uploadFile} onError={setError} />
            <CaptureButton icon="ri-attachment-2" label="Anexar arquivo" accept="image/*,video/*,audio/*" onChange={handleUpload} />
          </div>
        )}
        <p className="text-[11px] text-foreground-400">Vídeo até 150 MB; foto e áudio até 160 MB. A gravação é enviada direto ao servidor.</p>
        {error && <p className="text-xs text-danger-500">{error}</p>}
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2 mb-3">
          <i className="ri-gallery-line text-accent-500"></i>
          Evidências ({evidence.length})
        </h4>
        {evidence.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhuma evidência enviada.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {evidence.map((ev) => {
              const invalid = !!ev.invalidatedAt;
              return (
                <div
                  key={ev.id}
                  className={`relative rounded-lg border overflow-hidden bg-background-50 ${
                    invalid ? "border-danger-200 opacity-70" : "border-background-200/50"
                  }`}
                >
                  <EvidencePreview inspectionId={inspectionId} ev={ev} />
                  <div className="p-2 space-y-1">
                    <span className="text-[10px] uppercase font-bold text-foreground-400 tracking-wider">
                      {photoGroupLabel(ev.evidenceType) ? `${photoGroupLabel(ev.evidenceType)} · ` : ""}
                      {photoCategoryLabel(ev.evidenceType)}
                      {ev.fileSizeBytes ? ` · ${mb(ev.fileSizeBytes)}` : ""}
                    </span>
                    {ev.description && <p className="text-xs text-foreground-600 truncate">{ev.description}</p>}

                    {invalid ? (
                      <p className="text-[11px] text-danger-700 leading-snug">
                        <strong>Invalidada.</strong> {ev.invalidationReason}
                      </p>
                    ) : editable ? (
                      <button
                        type="button"
                        onClick={() => handleDelete(ev.id)}
                        className="mt-1 text-[11px] font-medium text-danger-600 hover:text-danger-700 flex items-center gap-1"
                      >
                        <i className="ri-delete-bin-line"></i> Excluir
                      </button>
                    ) : canInvalidate ? (
                      invalidating === ev.id ? (
                        <div className="space-y-1.5">
                          <textarea
                            rows={2}
                            autoFocus
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Motivo da invalidação (obrigatório)"
                            className="w-full text-xs bg-white border border-background-200 rounded px-2 py-1 resize-none"
                          />
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              disabled={reason.trim().length < 5}
                              onClick={() => handleInvalidate(ev.id)}
                              className="text-[11px] font-semibold px-2 py-1 rounded bg-danger-500 text-white disabled:opacity-50"
                            >
                              Invalidar
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setInvalidating(null);
                                setReason("");
                              }}
                              className="text-[11px] px-2 py-1 rounded text-foreground-500 hover:bg-background-100"
                            >
                              Voltar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setInvalidating(ev.id);
                            setReason("");
                          }}
                          className="mt-1 text-[11px] font-medium text-danger-600 hover:text-danger-700 flex items-center gap-1"
                        >
                          <i className="ri-prohibited-line"></i> Invalidar
                        </button>
                      )
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const captureCls =
  "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-primary-50/30 cursor-pointer transition-colors text-center";

function CaptureButton({
  icon,
  label,
  accept,
  capture,
  onChange,
}: {
  icon: string;
  label: string;
  accept: string;
  capture?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className={captureCls}>
      <i className={`${icon} text-xl text-primary-500`}></i>
      <span className="text-xs font-medium text-foreground-700">{label}</span>
      <input type="file" accept={accept} capture={capture ? "environment" : undefined} onChange={onChange} className="hidden" />
    </label>
  );
}

// Gravacao de audio no proprio navegador (MediaRecorder). O arquivo so existe
// em memoria ate ser enviado; nada fica salvo no aparelho.
function AudioRecorder({ onRecorded, onError }: { onRecorded: (f: File) => void; onError: (m: string) => void }) {
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!rec) return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [rec]);

  useEffect(() => () => rec?.stream.getTracks().forEach((tr) => tr.stop()), [rec]);

  const supported = typeof window !== "undefined" && "MediaRecorder" in window && !!navigator.mediaDevices?.getUserMedia;

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const type = (mr.mimeType || "audio/webm").split(";")[0];
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        if (chunks.length) onRecorded(new File(chunks, `audio_${stamp}.${ext}`, { type }));
      };
      mr.start();
      setSeconds(0);
      setRec(mr);
    } catch {
      onError("Sem acesso ao microfone. Libere o microfone para este site.");
    }
  };

  const stop = () => {
    rec?.stop();
    setRec(null);
  };

  if (!supported) {
    return (
      <div className={`${captureCls} opacity-50 cursor-not-allowed`} title="Este navegador não grava áudio">
        <i className="ri-mic-off-line text-xl text-foreground-400"></i>
        <span className="text-xs font-medium text-foreground-500">Gravar áudio</span>
      </div>
    );
  }
  return rec ? (
    <button type="button" onClick={stop} className={`${captureCls} border-danger-300 bg-danger-50`}>
      <i className="ri-stop-circle-line text-xl text-danger-500 animate-pulse"></i>
      <span className="text-xs font-medium text-danger-700">
        Parar {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
      </span>
    </button>
  ) : (
    <button type="button" onClick={() => void start()} className={captureCls}>
      <i className="ri-mic-line text-xl text-primary-500"></i>
      <span className="text-xs font-medium text-foreground-700">Gravar áudio</span>
    </button>
  );
}
